import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Package, Plus, LogOut, Truck, MapPin, User, Phone,
  Trash2, Edit, ChevronDown, X, CheckCircle, Box,
  Inbox, UserCheck, Mail, MessageSquare, Settings, DollarSign, MessageCircle, FileText, Database, Upload, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

// Import admin form components
import {
  CreateShipmentForm,
  EditShipmentForm,
  UpdateStatusForm,
  PricingSettingsForm,
  WhatsAppSettingsForm,
  EditCustomerForm,
  CreateCustomerForm,
  InvoiceForm,
  InvoiceView,
  PackageManagement,
  SplashSettingsForm,
  AdminManagement
} from "@/components/admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "RECEIVED", label: "Received", icon: Box, color: "bg-slate-500" },
  { value: "SHIPPED", label: "Shipped", icon: Package, color: "bg-blue-500" },
  { value: "IN_TRANSIT", label: "In Transit", icon: Truck, color: "bg-amber-500" },
  { value: "OUT_FOR_DELIVERY", label: "Out for Delivery", icon: MapPin, color: "bg-purple-500" },
  { value: "DELIVERED", label: "Delivered", icon: CheckCircle, color: "bg-amber-500" },
];

const AdminDashboard = () => {
  const [shipments, setShipments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [pricingSettings, setPricingSettings] = useState({ rate_per_lbs: 5, rate_per_volume: 5 });
  const [siteSettings, setSiteSettings] = useState({ whatsapp_number: '', whatsapp_message: '' });
  const [requests, setRequests] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [editShipmentOpen, setEditShipmentOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [editCustomerOpen, setEditCustomerOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceViewOpen, setInvoiceViewOpen] = useState(false);
  const [invoiceEditOpen, setInvoiceEditOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const username = localStorage.getItem("username");

  const axiosAuth = axios.create({
    headers: { Authorization: `Bearer ${token}` },
  });

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchAllData();
  }, [token, navigate]);

  const fetchAllData = async () => {
    try {
      const [shipmentsRes, requestsRes, customersRes, messagesRes, pricingRes, siteRes, invoicesRes] = await Promise.all([
        axiosAuth.get(`${API}/shipments`),
        axiosAuth.get(`${API}/requests`),
        axiosAuth.get(`${API}/customers`),
        axiosAuth.get(`${API}/contact/messages`),
        axiosAuth.get(`${API}/settings/pricing`),
        axios.get(`${API}/settings/site`),
        axiosAuth.get(`${API}/invoices`)
      ]);
      setShipments(shipmentsRes.data);
      setRequests(requestsRes.data);
      setCustomers(customersRes.data);
      setMessages(messagesRes.data);
      setPricingSettings(pricingRes.data);
      setSiteSettings(siteRes.data);
      setInvoices(invoicesRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("token");
        navigate("/admin");
      }
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    navigate("/admin");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this shipment?")) return;
    try {
      await axiosAuth.delete(`${API}/shipments/${id}`);
      toast.success("Shipment deleted");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete shipment");
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await axiosAuth.put(`${API}/requests/${requestId}/approve`);
      toast.success("Request approved and shipment created!");
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve request");
    }
  };

  const handleRejectRequest = async (requestId) => {
    const notes = prompt("Reason for rejection (optional):");
    try {
      await axiosAuth.put(`${API}/requests/${requestId}/reject?notes=${encodeURIComponent(notes || "")}`);
      toast.success("Request rejected");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to reject request");
    }
  };

  const getStatusConfig = (status) => STATUSES.find((s) => s.value === status) || STATUSES[0];

  const stats = {
    total: shipments.length,
    inTransit: shipments.filter((s) => s.status === "IN_TRANSIT").length,
    delivered: shipments.filter((s) => s.status === "DELIVERED").length,
    pendingRequests: requests.filter((r) => r.request_status === "PENDING").length,
    customers: customers.length,
    unreadMessages: messages.filter((m) => !m.is_read).length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-mono text-sm uppercase tracking-widest">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-v2.png" alt="SHIPP SMART" className="h-20 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm font-mono" data-testid="admin-username">@{username}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: "Shipments", value: stats.total, color: "bg-slate-900" },
            { label: "In Transit", value: stats.inTransit, color: "bg-amber-500" },
            { label: "Delivered", value: stats.delivered, color: "bg-amber-500" },
            { label: "Pending Requests", value: stats.pendingRequests, color: "bg-purple-500" },
            { label: "Customers", value: stats.customers, color: "bg-blue-500" },
            { label: "Unread Messages", value: stats.unreadMessages, color: "bg-red-500" },
          ].map((stat) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className={`w-2 h-2 ${stat.color} mb-3`} />
                  <p className="text-slate-500 text-[10px] uppercase tracking-widest mb-1">{stat.label}</p>
                  <p className="font-chivo font-black text-2xl text-slate-900">{stat.value}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="shipments" className="space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <TabsList className="bg-slate-200">
              <TabsTrigger value="shipments">Shipments</TabsTrigger>
              <TabsTrigger value="requests" data-testid="requests-tab">
                Requests {stats.pendingRequests > 0 && <Badge className="ml-2 bg-purple-500 text-white text-[10px]">{stats.pendingRequests}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="customers">Customers</TabsTrigger>
              <TabsTrigger value="packages" data-testid="packages-tab">
                <Package className="w-4 h-4 mr-1" /> Packages
              </TabsTrigger>
              <TabsTrigger value="messages">
                Messages {stats.unreadMessages > 0 && <Badge className="ml-2 bg-red-500 text-white text-[10px]">{stats.unreadMessages}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="settings-tab">
                <Settings className="w-4 h-4 mr-1" /> Settings
              </TabsTrigger>
            </TabsList>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest text-xs shadow-[4px_4px_0px_0px_rgba(16,185,129,0.3)] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
                  data-testid="create-shipment-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Shipment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl uppercase tracking-tight">Create Shipment</DialogTitle>
                </DialogHeader>
                <CreateShipmentForm
                  customers={customers}
                  onSuccess={() => {
                    setCreateDialogOpen(false);
                    fetchAllData();
                  }}
                />
              </DialogContent>
            </Dialog>
          </div>

          {/* Shipments Tab */}
          <TabsContent value="shipments">
            {shipments.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" strokeWidth={1} />
                  <h3 className="font-chivo font-bold text-lg text-slate-900 mb-2">No Shipments Yet</h3>
                  <p className="text-slate-500 mb-6">Create your first shipment to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="shipments-list">
                <AnimatePresence>
                  {shipments.map((shipment, index) => {
                    const statusConfig = getStatusConfig(shipment.status);
                    const StatusIcon = statusConfig.icon;

                    return (
                      <motion.div
                        key={shipment.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <Card className="border-slate-200 hover:border-slate-300 transition-colors">
                          <CardContent className="p-6">
                            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                              <div className="flex items-start gap-4">
                                <div className={`w-12 h-12 ${statusConfig.color}/10 flex items-center justify-center shrink-0`}>
                                  <StatusIcon className={`w-6 h-6 ${statusConfig.color.replace('bg-', 'text-')}`} strokeWidth={1.5} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="font-mono font-bold text-slate-900" data-testid={`tracking-${shipment.tracking_number}`}>
                                      {shipment.tracking_number}
                                    </span>
                                    <Badge className={`${statusConfig.color} text-white text-[10px] uppercase tracking-widest`}>
                                      {statusConfig.label}
                                    </Badge>
                                  </div>
                                  <p className="text-slate-600 text-sm">
                                    {shipment.origin} → {shipment.destination}
                                  </p>
                                  <p className="text-slate-400 text-xs mt-1">
                                    To: {shipment.receiver.name}, {shipment.receiver.city}
                                    {shipment.customer_id && <span className="ml-2 text-blue-500">(Customer assigned)</span>}
                                    {shipment.original_tracking && (
                                      <span className="ml-2 text-amber-600 font-mono">Carrier: {shipment.original_tracking}</span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="outline" size="sm" className="text-xs" data-testid={`update-status-${shipment.id}`}>
                                      Update Status
                                      <ChevronDown className="w-3 h-3 ml-2" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    {STATUSES.map((status) => (
                                      <DropdownMenuItem
                                        key={status.value}
                                        onClick={() => {
                                          setSelectedShipment(shipment);
                                          setUpdateDialogOpen(true);
                                        }}
                                        className="cursor-pointer"
                                      >
                                        <status.icon className="w-4 h-4 mr-2" />
                                        {status.label}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                {/* Edit Shipment Button */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedShipment(shipment);
                                    setEditShipmentOpen(true);
                                  }}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  data-testid={`edit-shipment-${shipment.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                {/* Invoice Button */}
                                {invoices.find(inv => inv.shipment_id === shipment.id) ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const inv = invoices.find(i => i.shipment_id === shipment.id);
                                      setSelectedInvoice(inv);
                                      setInvoiceViewOpen(true);
                                    }}
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    data-testid={`view-invoice-${shipment.id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    Invoice
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedShipment(shipment);
                                      setInvoiceDialogOpen(true);
                                    }}
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                    data-testid={`create-invoice-${shipment.id}`}
                                  >
                                    <FileText className="w-4 h-4 mr-1" />
                                    + Invoice
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(shipment.id)}
                                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                  data-testid={`delete-${shipment.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests">
            {requests.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Inbox className="w-16 h-16 text-slate-300 mx-auto mb-4" strokeWidth={1} />
                  <h3 className="font-chivo font-bold text-lg text-slate-900 mb-2">No Requests</h3>
                  <p className="text-slate-500">Customer shipment requests will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="requests-list">
                {requests.map((request, index) => (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className={`border-slate-200 ${request.request_status === "PENDING" ? "border-l-4 border-l-purple-500" : ""}`}>
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <Badge className={`${
                                request.request_status === "PENDING" ? "bg-amber-500" :
                                request.request_status === "APPROVED" ? "bg-amber-500" : "bg-red-500"
                              } text-white text-[10px] uppercase tracking-widest`}>
                                {request.request_status}
                              </Badge>
                              {request.estimated_price && (
                                <span className="text-slate-500 text-sm font-mono">Est. ${request.estimated_price}</span>
                              )}
                            </div>
                            <p className="font-medium text-slate-900">{request.origin} → {request.destination}</p>
                            <p className="text-slate-500 text-sm">From: {request.sender.name} | To: {request.receiver.name}</p>
                            <p className="text-slate-400 text-xs mt-1">
                              Customer: {request.customer_email} | {new Date(request.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {request.request_status === "PENDING" && (
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleApproveRequest(request.id)}
                                className="bg-amber-500 hover:bg-amber-600 text-white"
                                data-testid={`approve-${request.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRejectRequest(request.id)}
                                className="text-red-500 hover:bg-red-50"
                                data-testid={`reject-${request.id}`}
                              >
                                <X className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-slate-500 text-sm">Total: {customers.length} customers</p>
                <Button
                  onClick={() => setCreateCustomerOpen(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="add-customer-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Customer
                </Button>
              </div>
              {customers.length === 0 ? (
                <Card className="border-slate-200">
                  <CardContent className="p-12 text-center">
                    <UserCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" strokeWidth={1} />
                    <h3 className="font-chivo font-bold text-lg text-slate-900 mb-2">No Customers</h3>
                    <p className="text-slate-500">Registered customers will appear here</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4" data-testid="customers-list">
                  {customers.map((customer, index) => (
                    <motion.div
                      key={customer.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.02, 0.5) }}
                    >
                      <Card className="border-slate-200 hover:border-slate-300 transition-colors">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-500/10 flex items-center justify-center shrink-0 rounded">
                              <User className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate">{customer.name}</p>
                              <p className="text-slate-500 text-sm truncate">{customer.email}</p>
                              <p className="text-slate-400 text-xs truncate">{customer.phone || "No phone"} | {customer.address || "No address"}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <p className="text-slate-400 text-xs font-mono hidden md:block">
                                {new Date(customer.created_at).toLocaleDateString()}
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedCustomer(customer);
                                  setEditCustomerOpen(true);
                                }}
                                className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`edit-customer-${customer.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <PackageManagement />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            {messages.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" strokeWidth={1} />
                  <h3 className="font-chivo font-bold text-lg text-slate-900 mb-2">No Messages</h3>
                  <p className="text-slate-500">Contact form submissions will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="messages-list">
                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className={`border-slate-200 ${!message.is_read ? "border-l-4 border-l-red-500 bg-red-50/30" : ""}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-slate-200 flex items-center justify-center shrink-0">
                              <Mail className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium text-slate-900">{message.name}</p>
                                {!message.is_read && <Badge className="bg-red-500 text-white text-[10px]">New</Badge>}
                              </div>
                              <p className="text-slate-500 text-sm">{message.email} | {message.phone}</p>
                              <p className="text-slate-700 mt-2">{message.message}</p>
                            </div>
                          </div>
                          <p className="text-slate-400 text-xs font-mono shrink-0">
                            {new Date(message.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-amber-500" />
                      Pricing Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PricingSettingsForm 
                      settings={pricingSettings} 
                      onSuccess={(newSettings) => {
                        setPricingSettings(newSettings);
                      }} 
                    />
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-orange-500" />
                      Splash Screen Settings
                    </CardTitle>
                    <p className="text-slate-500 text-sm mt-1">Customize the welcome popup shown to website visitors</p>
                  </CardHeader>
                  <CardContent>
                    <SplashSettingsForm />
                  </CardContent>
                </Card>
              </div>
              <div className="space-y-6">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-green-500" />
                      WhatsApp Settings
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <WhatsAppSettingsForm 
                      settings={siteSettings} 
                      onSuccess={(newSettings) => {
                        setSiteSettings(newSettings);
                      }} 
                    />
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-500" />
                      Admin Accounts
                    </CardTitle>
                    <p className="text-slate-500 text-sm mt-1">Manage admin users and passwords</p>
                  </CardHeader>
                  <CardContent>
                    <AdminManagement />
                  </CardContent>
                </Card>
                <Card className="border-slate-200 bg-slate-50">
                  <CardHeader>
                    <CardTitle className="font-chivo uppercase tracking-tight text-slate-500">How Pricing Works</CardTitle>
                  </CardHeader>
                  <CardContent className="text-slate-600 space-y-3">
                    <p>The quote calculator uses the higher of:</p>
                    <ul className="list-disc list-inside space-y-2">
                      <li><strong>Weight Cost:</strong> Weight (lbs) × Rate per lbs</li>
                      <li><strong>Volume Cost:</strong> Volume (ft³) × Rate per volume</li>
                    </ul>
                    <p className="text-sm text-slate-500 mt-4">
                      Whichever calculation results in a higher price will be used as the final quote.
                    </p>
                  </CardContent>
                </Card>
                <DatabaseRestoreCard token={token} />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Update Status Dialog */}
        <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-chivo font-bold uppercase tracking-tight">Update Status</DialogTitle>
            </DialogHeader>
            {selectedShipment && (
              <UpdateStatusForm
                shipment={selectedShipment}
                onSuccess={() => {
                  setUpdateDialogOpen(false);
                  setSelectedShipment(null);
                  fetchAllData();
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Customer Dialog */}
        <Dialog open={editCustomerOpen} onOpenChange={setEditCustomerOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-chivo font-bold uppercase tracking-tight">Edit Customer</DialogTitle>
            </DialogHeader>
            {selectedCustomer && (
              <EditCustomerForm
                customer={selectedCustomer}
                onSuccess={(updatedCustomer) => {
                  setCustomers(customers.map(c => c.id === updatedCustomer.id ? updatedCustomer : c));
                  setEditCustomerOpen(false);
                  setSelectedCustomer(null);
                  toast.success("Customer updated successfully!");
                }}
                onDelete={(customerId) => {
                  setCustomers(customers.filter(c => c.id !== customerId));
                  setEditCustomerOpen(false);
                  setSelectedCustomer(null);
                  toast.success("Customer deleted successfully!");
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Create Customer Dialog */}
        <Dialog open={createCustomerOpen} onOpenChange={setCreateCustomerOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-chivo font-bold uppercase tracking-tight">Add New Customer</DialogTitle>
            </DialogHeader>
            <CreateCustomerForm
              onSuccess={(newCustomer) => {
                setCustomers([...customers, newCustomer]);
                setCreateCustomerOpen(false);
                toast.success("Customer created successfully!");
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Create Invoice Dialog */}
        <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo font-bold uppercase tracking-tight flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                Create Invoice
              </DialogTitle>
            </DialogHeader>
            {selectedShipment && (
              <InvoiceForm
                shipment={selectedShipment}
                onSuccess={() => {
                  setInvoiceDialogOpen(false);
                  setSelectedShipment(null);
                  fetchAllData();
                }}
                onCancel={() => {
                  setInvoiceDialogOpen(false);
                  setSelectedShipment(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* View Invoice Dialog */}
        <Dialog open={invoiceViewOpen} onOpenChange={setInvoiceViewOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
            {selectedInvoice && (
              <InvoiceView
                invoice={selectedInvoice}
                onClose={() => {
                  setInvoiceViewOpen(false);
                  setSelectedInvoice(null);
                }}
                onUpdate={(updatedInvoice) => {
                  setSelectedInvoice(updatedInvoice);
                  setInvoices(invoices.map(inv => 
                    inv.id === updatedInvoice.id ? updatedInvoice : inv
                  ));
                }}
                onEdit={(inv) => {
                  setInvoiceViewOpen(false);
                  setSelectedInvoice(inv);
                  const shipment = shipments.find(s => s.id === inv.shipment_id);
                  if (shipment) setSelectedShipment(shipment);
                  setInvoiceEditOpen(true);
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Invoice Dialog */}
        <Dialog open={invoiceEditOpen} onOpenChange={setInvoiceEditOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo font-bold text-xl uppercase tracking-tight flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-500" />
                Edit Invoice
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <InvoiceForm
                shipment={selectedShipment}
                existingInvoice={selectedInvoice}
                onSuccess={() => {
                  setInvoiceEditOpen(false);
                  setSelectedInvoice(null);
                  fetchAllData();
                }}
                onCancel={() => {
                  setInvoiceEditOpen(false);
                  setSelectedInvoice(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Shipment Dialog */}
        <Dialog open={editShipmentOpen} onOpenChange={setEditShipmentOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo font-bold text-xl uppercase tracking-tight flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-500" />
                Edit Shipment
              </DialogTitle>
            </DialogHeader>
            {selectedShipment && (
              <EditShipmentForm
                shipment={selectedShipment}
                customers={customers}
                onSuccess={() => {
                  setEditShipmentOpen(false);
                  setSelectedShipment(null);
                  fetchAllData();
                }}
                onCancel={() => {
                  setEditShipmentOpen(false);
                  setSelectedShipment(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

const DatabaseRestoreCard = ({ token }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleRestore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".zip")) {
      toast.error("Please upload a ZIP file");
      return;
    }
    if (!window.confirm("This will REPLACE all current database data. Are you sure?")) {
      e.target.value = "";
      return;
    }
    setUploading(true);
    setResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/db/restore`, formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
      toast.success("Database restored successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restore failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Card className="border-red-200 bg-red-50/30">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2 text-base">
          <Database className="w-5 h-5 text-red-500" />
          Database Restore
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-slate-600 text-sm">
          Upload a ZIP file containing JSON collection exports to restore the database.
        </p>
        <label
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold text-xs uppercase tracking-widest cursor-pointer transition-colors"
          data-testid="db-restore-btn"
        >
          <Upload className="w-4 h-4" />
          {uploading ? "Restoring..." : "Upload & Restore"}
          <input
            type="file"
            accept=".zip"
            onChange={handleRestore}
            className="hidden"
            disabled={uploading}
            data-testid="db-restore-input"
          />
        </label>
        {result && (
          <div className="mt-3 p-3 bg-white border border-slate-200 rounded text-sm space-y-1" data-testid="db-restore-result">
            <p className="font-bold text-green-600">{result.message}</p>
            {Object.entries(result.collections).map(([name, info]) => (
              <p key={name} className="text-slate-600 font-mono text-xs">
                {name}: {info.documents} docs
              </p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;
